import React, { useState, useRef, FC, PropsWithChildren, useEffect } from "react";
import { useSpring, animated } from "@react-spring/web";
import {
	NativeProps,
	withNativeProps,
	mergeProps,
	renderToContainer,
	withStopPropagation,
	ShouldRender,
} from "@/utils/components-utils";
import Aperture from "@/components/Aperture";
import { defaultPopupBaseProps, PopupBaseProps } from "./popup-base-props";

import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import useUnmountedRef from "@/hooks/useUnmountedRef";
import { useLockScroll } from "@/hooks/useLockScroll";
import { useInnerVisible } from "@/hooks/useInnerVisible";
import { EventTypes, FullGestureState, useDrag } from "@use-gesture/react";
import Overlay from "../overlay/Overlay";
import { isIos } from "@/utils/parserDevice";

export type PopupProps = PopupBaseProps &
	PropsWithChildren<{
		position?: "bottom" | "top" | "left" | "right";
	}> &
	NativeProps;

const defaultProps = {
	...defaultPopupBaseProps,
	position: "bottom",
};

export const Popup: FC<PopupProps> = (p) => {
	const props = mergeProps(defaultProps, p);

	const [active, setActive] = useState(props.visible);
	useIsomorphicLayoutEffect(() => {
		if (props.visible) {
			setActive(true);
			document.getElementById("__next")?.classList.add("filter__bg");
			document.getElementById("portal")?.classList.add("filter__bg");
		} else {
			if (typeof window !== "undefined") {
				document.getElementById("__next")?.classList.remove("filter__bg");
				document.getElementById("portal")?.classList.remove("filter__bg");
			}
		}

		return () => {
			if (typeof window !== "undefined") {
				document.getElementById("__next")?.classList.remove("filter__bg");
				document.getElementById("portal")?.classList.remove("filter__bg");
			}
		};
	}, [props.visible]);

	const ref = useRef<HTMLDivElement>(null);
	useLockScroll(ref, props.disableBodyScroll && active ? "strict" : false);

	const unmountedRef = useUnmountedRef();
	const { percent } = useSpring({
		percent: props.visible ? 0 : 100,
		config: {
			precision: 0.2,
			mass: 0.4,
			tension: 100,
			friction: 11,
			clamp: true,
		},
		onRest: () => {
			if (unmountedRef.current) return;
			setActive(props.visible);
			if (props.visible) {
				props.afterShow?.();
			} else {
				props.afterClose?.();
			}
		},
	});

	const draggableRef = useRef<HTMLDivElement>(null);
	const [aperturePosition, setAperturePosition] = useState<number>(0);
	const [apertureOpacity, setApertureOpacity] = useState<number>(0.8);

	const [initHeight, setInitHeight] = useState(0);

	useEffect(() => {
		if (!active) return;
		if (!ref.current) return;

		const rootClientHeight = ref.current?.clientHeight ?? 0;
		const winHeight = window.innerHeight;
		setInitHeight(rootClientHeight);
		setAperturePosition((winHeight - rootClientHeight) / 0.98);
	}, [active]);

	useEffect(() => {
		const handler = () => {
			setTimeout(() => {
				if (!ref.current) return;

				const rootClientHeight = ref.current?.clientHeight ?? 0;
				const winHeight = window.innerHeight;
				setInitHeight(rootClientHeight);
				setAperturePosition((winHeight - rootClientHeight) / 0.98);
			}, 100);
		};

		if (isIos()) return;

		window.addEventListener("resize", handler);
		return () => {
			window.removeEventListener("resize", handler);
		};
	}, []);

	const handleDrag = (
		state: Omit<FullGestureState<"drag">, "event"> & {
			event: EventTypes["drag"];
		},
	) => {
		if (ref.current && ref.current.style) {
			// 最大和最小高度可根据
			const viewportHeight = window.innerHeight;
			const maxHeight = initHeight || viewportHeight * 0.8;
			const minHeight = viewportHeight * 0.1;
			let newHeight = viewportHeight - state.offset[1];

			// 阈值设定为视口高度的5%，可自行调整百分比
			let threshold = viewportHeight * 0.05;

			// 关闭弹窗的速度阈值
			let closeSpeed = 1.0;

			if (state.last) {
				// 如果你结束拖动
				ref.current.style.height = `${initHeight}px`; // 初始高度
				setApertureOpacity(0.8);
				return;
			}

			const distance = state.offset[1] - state.initial[1];
			if (state.velocity[1] > closeSpeed && distance > threshold) {
				// 如果用户下拉的速度快，且下拉的距离超过阈值，则关闭弹窗
				props.onMaskClick?.(null as any);
				props.onClose?.();
			} else {
				// 否则，正常调整弹窗的高度
				// 检查元素高度是否在限定的最大最小范围内
				if (newHeight > maxHeight) {
					newHeight = maxHeight;
				} else if (newHeight < minHeight) {
					newHeight = minHeight;
				}

				ref.current.style.height = `${newHeight}px`;
				let opacity = (0.8 * newHeight) / maxHeight;
				opacity = isNaN(opacity) ? 0.8 : opacity;
				setApertureOpacity(opacity);
			}
		}
	};

	useDrag(
		(state) => {
			state.event.stopPropagation();
			handleDrag(state);
		},
		{
			axis: "y",
			from: () => [0, window.innerHeight - (ref.current?.clientHeight ?? 0)],
			filterTaps: true,
			pointer: { touch: true },
			target: draggableRef,
		},
	);

	const maskVisible = useInnerVisible(active && props.visible);

	const node = withStopPropagation(
		props.stopPropagation,
		withNativeProps(
			props,
			<div onClick={props.onClick} style={{ display: active ? undefined : "none" }}>
				{props.mask && (
					<Overlay
						visible={maskVisible}
						onClose={(e: any) => {
							props.onMaskClick?.(e);
							if (props.closeOnMaskClick) {
								props.onClose?.();
							}
						}}
					>
						<Aperture
							onClick={(e) => {
								props.onMaskClick?.(e);
							}}
							style={{
								top: aperturePosition + "px",
								opacity: apertureOpacity,
							}}
						/>
					</Overlay>
				)}
				<animated.div
					className="fixed bg-white dark:bg-dark rounded-t-[2rem] z-[1010] w-full left-0 bottom-0"
					style={{
						transform: percent.to((v) => `translate3d(0, ${v}%, 0)`),
					}}
					ref={ref}
				>
					<div className="w-full pb-7 pt-3.5 text-center text-[0px]" ref={draggableRef}>
						<div className="inline-block h-[5px] w-9 rounded bg-c3c3c3 dark:bg-606060" />
					</div>

					{props.children}
				</animated.div>
			</div>,
		),
	);

	return (
		<ShouldRender active={active} forceRender={props.forceRender} destroyOnClose={props.destroyOnClose}>
			{renderToContainer(props.getContainer, node)}
		</ShouldRender>
	);
};
